import { useMemo } from 'react';
import { switchcase } from '@sweetgreen/garnish';

import { CONTENTFUL_TAGS } from '@order/constants';
import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useLocalizationContext } from '@order/Localization';

/**
 * Returns contentful data from 'Sweetpass Account Page'.
 * Falls back to localized strings.
 * @see: (@link: https://app.contentful.com/spaces/wme4s8lvzccr/content_types/accountSweetpassPage)
 */
export const useSweetpassAccountContentfulData = () => {
  const { t } = useLocalizationContext();

  const { data: contentfulData, fetching: isLoadingContentfulData } =
    useContentfulContentTypeEntry<ContentfulSweetpassAccountPageFields>({
      contentType: SWEETPASS_MEMBERSHIP_CONTENTFUL_CONTENT_ID,
      include: 2,
    });

  return {
    isLoadingContentfulData,
    content: useMemo(() => {
      const remoteContent = contentfulData?.fields?.content ?? [];
      const remoteSections = remoteContent?.map(
        ({ fields }) => fields.name,
      ) as readonly string[];
      const missingSections = REQUIRED_SECTIONS.filter(
        (section: string) => !remoteSections.includes(section),
      );
      const content = [
        ...remoteContent,
        ...missingSections.map((name: string) => ({ fields: { name } })),
      ];

      return content?.map(({ fields }) => {
        const contentfulFields = fields as ContentfulSweetpassAccountPageData;

        return switchcase({
          [ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION]:
            fallbackMembershipContentfulValues(t, contentfulFields),
          [ACCOUNT_SWEETPASS_CANCELLATION_SECTION]:
            fallbackCancellationContentfulValues(t, contentfulFields),
          [ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION]:
            fallbackPaymentMethodsContentfulValues(t, contentfulFields),
          [ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION]:
            fallbackBillingHistoryContentfulValues(t, contentfulFields),
        })({} as never)(fields?.name ?? '');
      });
    }, [contentfulData?.fields?.content, t]),
  };
};

// ─── Utils ────────────────────────────────────────────────────────

const fallbackMembershipContentfulValues = (
  t: ReturnType<typeof useLocalizationContext>['t'],
  data: ContentfulSweetpassAccountPageData,
) => {
  const variedBody = data?.body as VariedFieldArray;

  const sweetpassBody = variedBody?.find?.((entry) => {
    const tagIds = entry.metadata?.tags?.map((tag) => tag?.sys?.id);

    return (
      tagIds?.includes(CONTENTFUL_TAGS.Tier.Sweetpass) &&
      !tagIds?.includes(CONTENTFUL_TAGS.Status.Downgraded)
    );
  })?.fields;

  const sweetpassDowngradedBody = variedBody?.find?.((entry) => {
    const tagIds = entry.metadata?.tags?.map((tag) => tag?.sys?.id);

    return (
      tagIds?.includes(CONTENTFUL_TAGS.Tier.Sweetpass) &&
      tagIds?.includes(CONTENTFUL_TAGS.Status.Downgraded)
    );
  })?.fields;

  const sweetpassPlusBody = variedBody?.find?.((entry) => {
    const tagIds = entry.metadata?.tags?.map((tag) => tag?.sys?.id);

    return (
      tagIds?.includes(CONTENTFUL_TAGS.Tier.SweetpassPlus) &&
      !tagIds?.includes(CONTENTFUL_TAGS.Status.Cancelled)
    );
  })?.fields;

  const sweetpassPlusCancelledBody = variedBody?.find?.((entry) => {
    const tagIds = entry.metadata?.tags?.map((tag) => tag?.sys?.id);

    return (
      tagIds?.includes(CONTENTFUL_TAGS.Tier.SweetpassPlus) &&
      tagIds?.includes(CONTENTFUL_TAGS.Status.Cancelled)
    );
  })?.fields;

  return {
    name: ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION,
    membershipDataTitle: data?.title ?? t('membershipData.title'),
    membershipDataHeader: data?.header ?? t('membershipData.header'),

    membershipDataBodySweetpass:
      sweetpassBody?.value ?? t('membershipData.body.sweetpass'),
    membershipDataCtaSweetpass:
      sweetpassBody?.cta ??
      t('account.sweetpass-membership.membership-level.sweetpass.cta.title'),

    membershipDataBodySweetpassDowngraded: sweetpassDowngradedBody?.value ?? '',
    membershipDataCtaSweetpassDowngraded:
      sweetpassDowngradedBody?.cta ??
      t(
        'account.sweetpass-membership.membership-level.sweetpass-downgraded.cta.title',
      ),

    membershipDataBodySweetpassPlus:
      sweetpassPlusBody?.value ?? t('membershipData.body.sweetpassPlus'),
    membershipDataCtaSweetpassPlus: sweetpassPlusBody?.cta,

    membershipDataBodySweetpassPlusCancelled:
      sweetpassPlusCancelledBody?.value ??
      t('membershipData.body.sweetpassPlusCancelled'),
    membershipDataCtaSweetpassPlusCancelled:
      sweetpassPlusCancelledBody?.cta ??
      t(
        'account.sweetpass-membership.membership-level.sweetpass-plus-canceled.cta.title',
      ),
  };
};

const fallbackCancellationContentfulValues = (
  t: ReturnType<typeof useLocalizationContext>['t'],
  data: ContentfulSweetpassAccountPageData,
) => {
  return {
    name: ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
    cancellationDataTitle: data?.title ?? t('cancellationData.title'),
    cancellationDataBody: data?.body ?? t('cancellationData.body'),
    cancellationDataCancellationCta:
      data?.cancellationCta ?? t('cancellationData.cancellationCta'),
    cancellationDataGenericSuccessMessage:
      data?.genericSuccessMessage ??
      t('cancellationData.genericSuccessMessage'),
    cancellationDataGenericErrorMessage:
      data?.genericErrorMessage ?? t('sweetpass-membership.cancellation.error'),
  };
};

const fallbackPaymentMethodsContentfulValues = (
  t: ReturnType<typeof useLocalizationContext>['t'],
  data: ContentfulSweetpassAccountPageData,
) => {
  return {
    name: ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION,
    paymentMethodsDataTitle: data?.title ?? t('paymentMethodsData.title'),
  };
};

const fallbackBillingHistoryContentfulValues = (
  t: ReturnType<typeof useLocalizationContext>['t'],
  data: ContentfulSweetpassAccountPageData,
) => {
  return {
    name: ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION,
    billingHistoryDataTitle: data?.title ?? t('billingHistoryData.title'),
  };
};

// ─── Constants ────────────────────────────────────────────────────

const SWEETPASS_MEMBERSHIP_CONTENTFUL_CONTENT_ID = 'accountSweetpassPage';

export const ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION =
  'Account Sweetpass Membership Section';
export const ACCOUNT_SWEETPASS_CANCELLATION_SECTION =
  'Account Sweetpass Cancellation Section';
export const ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION =
  'Account Sweetpass Payment Methods Section';
export const ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION =
  'Account Sweetpass Billing History Section';

const REQUIRED_SECTIONS = [
  ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION,
  ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
  ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION,
  ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION,
];

// ─── Types ────────────────────────────────────────────────────────

type ContentfulSweetpassAccountPageFields = {
  readonly content: ReadonlyArray<
    | {
        readonly fields: ContentfulSweetpassMembershipSectionFields;
      }
    | {
        readonly fields: ContentfulSweetpassCancellationSectionFields;
      }
    | {
        readonly fields: ContentfulSweetpassPaymentMethodsSectionFields;
      }
    | {
        readonly fields: ContentfulSweetpassBillingHistorySectionFields;
      }
  >;
};

type ContentfulSweetpassAccountPageData = Omit<
  ContentfulSweetpassMembershipSectionFields,
  'name'
> &
  Omit<ContentfulSweetpassCancellationSectionFields, 'name'> &
  Omit<ContentfulSweetpassPaymentMethodsSectionFields, 'name'> &
  Omit<ContentfulSweetpassBillingHistorySectionFields, 'name'>;

type ContentfulSweetpassMembershipSectionFields = {
  readonly name?: typeof ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION;
  readonly title?: string;
  readonly header?: string;
  readonly body?: VariedFieldArray | string;
};

type ContentfulSweetpassCancellationSectionFields = {
  readonly name?: typeof ACCOUNT_SWEETPASS_CANCELLATION_SECTION;
  readonly title?: string;
  readonly body?: VariedFieldArray | string;
  readonly cancellationCta?: string;
  readonly genericSuccessMessage?: string;
  readonly genericErrorMessage?: string;
};

type ContentfulSweetpassPaymentMethodsSectionFields = {
  readonly name?: typeof ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION;
  readonly title?: string;
};

type ContentfulSweetpassBillingHistorySectionFields = {
  readonly name?: typeof ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION;
  readonly title?: string;
};

type VariedFieldArray = ReadonlyArray<{
  readonly fields: {
    readonly name?: string;
    readonly value?: string;
    readonly cta?: string;
  };
  readonly metadata: { readonly tags: readonly ContentfulTag[] };
}>;

type ContentfulTag = { readonly sys?: { readonly id?: string } };

export type AccountSweetpassMembershipSection = ReturnType<
  typeof fallbackMembershipContentfulValues
>;
export type AccountSweetpassCancellationSection = ReturnType<
  typeof fallbackCancellationContentfulValues
>;
export type AccountSweetpassPaymentMethodsSection = ReturnType<
  typeof fallbackPaymentMethodsContentfulValues
>;
export type AccountSweetpassBillingHistorySection = ReturnType<
  typeof fallbackBillingHistoryContentfulValues
>;
